/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Link as ReactRouterLink, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Heading,
  HStack,
  Icon,
  Link,
  Text,
  Stack,
} from '@chakra-ui/react';
import { FaCheck, FaCircle } from 'react-icons/fa';

import {
  ALL_PAYMENTS_FOR_REGISTRATION_OPTION_AGAINST_EVENT,
  CATEGORY_WITH_EVENT_COMPETITIONS,
  FETCH_EVENT,
  FETCH_REG_SPREADSHEET_COACHES,
  FETCH_REG_SPREADSHEET_GUESTS,
  FETCH_REG_SPREADSHEET_STUDENTS,
} from '../../GraphQL/Queries';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useCurrentUser from '../../Hooks/userCurrentUser';
import Loader from '../../Components/Loader';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import { Provider } from '../../Chakra/provider';

const MIST_BLUE = '#3E6796';
const MIST_GOLD = '#F4AB37';

const StatusIcon = ({ isComplete }) => {
  if (isComplete) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="24px"
        height="24px"
        borderRadius="50%"
        bg="green.500"
      >
        <Icon as={FaCheck} color="white" boxSize="3" />
      </Box>
    );
  }
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="24px"
      height="24px"
      border="2px solid"
      borderColor={MIST_GOLD}
      borderRadius="50%"
    >
      <Icon as={FaCircle} color={MIST_GOLD} boxSize="2" />
    </Box>
  );
};

// Shared header for checklist items to reduce duplication across cards
const ChecklistHeader = ({ itemNumber, title, description, isComplete }) => (
  <HStack spacing={3} flex={1}>
    <Box minW="24px">
      <StatusIcon isComplete={isComplete} />
    </Box>
    <Box flex={1}>
      <HStack spacing={2}>
        <Badge variant="solid" colorScheme={isComplete ? 'green' : 'orange'}>
          {itemNumber}
        </Badge>
        <Box>
          <Text fontWeight="semibold" color={MIST_BLUE}>{title}</Text>
          {description && <Text fontSize="sm" color="gray.600">{description}</Text>}
        </Box>
      </HStack>
    </Box>
  </HStack>
);

const ExpandableChecklistCard = ({
  title, description, count, linkTo, linkText, details, itemNumber,
}) => {
  const isComplete = count === 0;

  return (
    <Box
      borderLeft="4px solid"
      borderLeftColor={isComplete ? 'green.500' : MIST_GOLD}
      bg={isComplete ? 'green.50' : 'orange.50'}
      p={4}
      borderRadius="md"
      transition="all 0.2s"
      _hover={{ shadow: 'md' }}
    >
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton p={0} _hover={{ bg: 'transparent' }} _focus={{ boxShadow: 'none', outline: 'none' }} _focusVisible={{ outline: 'none' }} textAlign="left">
            <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" spacing={4} align={{ base: 'flex-start', md: 'center' }} w="100%">
              <ChecklistHeader
                itemNumber={itemNumber}
                title={title}
                description={description}
                isComplete={isComplete}
              />
              <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'center', md: 'center' }}>
                {isComplete ? (
                  <Badge colorScheme="green" fontSize="xs">Complete</Badge>
                ) : (
                  <HStack spacing={1}>
                    <Badge colorScheme="orange" fontSize="xs">{count} pending</Badge>
                    <AccordionIcon />
                  </HStack>
                )}
              </Stack>
            </Stack>
          </AccordionButton>
          {!isComplete && (
            <AccordionPanel pt={4} pl={10}>
              <Stack align="stretch" spacing={2}>
                {details.map((detail) => (
                  <HStack key={detail.label} justify="space-between">
                    <Text fontSize="sm">{detail.label}</Text>
                    <Badge colorScheme="orange">{detail.count}</Badge>
                  </HStack>
                ))}
                <Divider my={2} />
                <Link as={ReactRouterLink} to={linkTo} color={MIST_BLUE} fontSize="sm" fontWeight="semibold">
                  {linkText} →
                </Link>
              </Stack>
            </AccordionPanel>
          )}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

const SimpleChecklistCard = ({ // not expandable
  title, description, count, linkTo, itemNumber,
}) => {
  const isComplete = count === 0;

  return (
    <Box
      borderLeft="4px solid"
      borderLeftColor={isComplete ? 'green.500' : MIST_GOLD}
      bg={isComplete ? 'green.50' : 'orange.50'}
      p={4}
      borderRadius="md"
      transition="all 0.2s"
      _hover={{ shadow: 'md' }}
    >
      <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" spacing={4} align={{ base: 'flex-start', md: 'center' }}>
        <ChecklistHeader
          itemNumber={itemNumber}
          title={title}
          description={description}
          isComplete={isComplete}
        />
        <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'center', md: 'center' }}>
          {isComplete ? (
            <Badge colorScheme="green" fontSize="xs">Complete</Badge>
          ) : (
            <>
              <Badge colorScheme="orange" fontSize="xs">{count} pending</Badge>
              <Link as={ReactRouterLink} to={linkTo}>
                <Button size="sm" colorScheme="orange">
                  Review
                </Button>
              </Link>
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const WaitlistCard = ({ totalWaitlisted, waitlistedByCompetition, eventId, itemNumber }) => {
  const isComplete = totalWaitlisted === 0;

  return (
    <Box
      borderLeft="4px solid"
      borderLeftColor={isComplete ? 'green.500' : MIST_GOLD}
      bg={isComplete ? 'green.50' : 'orange.50'}
      p={4}
      borderRadius="md"
      transition="all 0.2s"
      _hover={{ shadow: 'md' }}
    >
      <Accordion allowToggle>
        <AccordionItem border="none">
          <AccordionButton p={0} _hover={{ bg: 'transparent' }} _focus={{ boxShadow: 'none', outline: 'none' }} _focusVisible={{ outline: 'none' }} textAlign="left">
            <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" spacing={4} align={{ base: 'flex-start', md: 'center' }} w="100%">
              <ChecklistHeader
                itemNumber={itemNumber}
                title="Competition Waitlists"
                description={isComplete
                  ? 'No waitlisted participants'
                  : `${waitlistedByCompetition.length} competition${waitlistedByCompetition.length !== 1 ? 's' : ''} need attention`}
                isComplete={isComplete}
              />
              <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'center', md: 'center' }}>
                {isComplete ? (
                  <Badge colorScheme="green" fontSize="xs">Complete</Badge>
                ) : (
                  <HStack spacing={1}>
                    <Badge colorScheme="orange" fontSize="xs">{totalWaitlisted} pending</Badge>
                    <AccordionIcon />
                  </HStack>
                )}
              </Stack>
            </Stack>
          </AccordionButton>
          {!isComplete && (
            <AccordionPanel pt={4} pl={10}>
              <Stack align="stretch" spacing={2}>
                {waitlistedByCompetition.map((comp) => (
                  <Link
                    key={comp.id}
                    as={ReactRouterLink}
                    to={`/events/${eventId}/competitions/${comp.id}`}
                    _hover={{ textDecoration: 'none' }}
                  >
                    <HStack justify="space-between" p={2} borderRadius="md" _hover={{ bg: 'gray.100' }}>
                      <Text fontSize="sm" fontWeight="500">{comp.title}</Text>
                      <Badge colorScheme="orange">
                        {`${comp.waitlistedCount} waiting`}
                      </Badge>
                    </HStack>
                  </Link>
                ))}
                <Divider my={2} />
                <Link
                  as={ReactRouterLink}
                  to={`/events/${eventId}/competitions-report`}
                  color={MIST_BLUE}
                  fontSize="sm"
                  fontWeight="semibold"
                >
                  View full competitions report →
                </Link>
              </Stack>
            </AccordionPanel>
          )}
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

const countByStatus = (eventUsers, predicate) => eventUsers.filter(predicate).length;

const computeRegistrationCounts = (students, coaches, guests) => {
  const isPending = (eventUser) => eventUser.status !== 'active';
  const hasNoSchool = (eventUser) => !eventUser.fetchSchool;

  return {
    pendingApproval: {
      total: countByStatus(students, isPending)
        + countByStatus(coaches, isPending)
        + countByStatus(guests, isPending),
      details: [
        { label: 'Students', count: countByStatus(students, isPending) },
        { label: 'Coaches', count: countByStatus(coaches, isPending) },
        { label: 'Guests', count: countByStatus(guests, isPending) },
      ],
    },
    noSchool: {
      total: countByStatus(students, hasNoSchool)
        + countByStatus(coaches, hasNoSchool)
        + countByStatus(guests, hasNoSchool),
      details: [
        { label: 'Students', count: countByStatus(students, hasNoSchool) },
        { label: 'Coaches', count: countByStatus(coaches, hasNoSchool) },
        { label: 'Guests', count: countByStatus(guests, hasNoSchool) },
      ],
    },
  };
};

const computeUnpaidCounts = (registrationOptions) => {
  const byOption = [];
  let total = 0;

  registrationOptions.forEach((option) => {
    const unpaidUsers = option.paymentUser?.unpaid_user ?? [];
    if (unpaidUsers.length > 0) {
      byOption.push({ label: option.title || 'Registration Option', count: unpaidUsers.length });
      total += unpaidUsers.length;
    }
  });

  return { total, details: byOption };
};

const computeWaitlistData = (competitionsData) => {
  if (!competitionsData) return { total: 0, byCompetition: [] };

  const categories = competitionsData.categoryWithEventCompetitions ?? [];
  const allCompetitions = categories.flatMap((cat) => cat.eventCompetitions || []);

  let total = 0;
  const byCompetition = [];

  allCompetitions.forEach((comp) => {
    const users = comp.users ?? [];
    const waitlistedCount = users.filter((u) => u.member?.status === 'waitlist').length;
    if (waitlistedCount > 0) {
      byCompetition.push({
        id: comp.id,
        title: comp.title,
        waitlistedCount,
      });
      total += waitlistedCount;
    }
  });

  return { total, byCompetition };
};

const TOTAL_CHECKLIST_ITEMS = 6;

const ProgressIndicator = ({ completedCount }) => {
  const allComplete = completedCount === TOTAL_CHECKLIST_ITEMS;
  const remaining = TOTAL_CHECKLIST_ITEMS - completedCount;
  const percentage = (completedCount / TOTAL_CHECKLIST_ITEMS) * 100;

  return (
    <Box
      bg="white"
      p={6}
      borderRadius="lg"
      boxShadow="md"
      borderTop="4px solid"
      borderTopColor={allComplete ? 'green.500' : MIST_BLUE}
    >
      <Stack spacing={4}>
        <HStack spacing={6} justify="center">
          <CircularProgress
            value={percentage}
            size="120px"
            color={allComplete ? 'green.500' : MIST_BLUE}
            thickness={4}
          >
            <CircularProgressLabel fontSize="xl" fontWeight="bold">
              {completedCount}/{TOTAL_CHECKLIST_ITEMS}
            </CircularProgressLabel>
          </CircularProgress>
          <Box>
            <Heading size="md" color={MIST_BLUE}>
              {allComplete ? '✓ All Set!' : 'In Progress'}
            </Heading>
            <Text fontSize="sm" color="gray.600" mt={2}>
              {allComplete
                ? "You're ready for your tournament!"
                : `${remaining} item${remaining !== 1 ? 's' : ''} need${remaining === 1 ? 's' : ''} attention`}
            </Text>
          </Box>
        </HStack>
      </Stack>
    </Box>
  );
};

const PreTournamentChecklist = () => {
  const { currentUser } = useCurrentUser(true);
  const { id } = useParams();
  const eventId = parseInt(id, 10);
  const skip = !currentUser;

  const { data: eventData, loading: eventLoading } = useQuery(FETCH_EVENT, {
    variables: { id: eventId },
    skip,
  });
  const { data: studentsData, loading: studentsLoading } = useQuery(
    FETCH_REG_SPREADSHEET_STUDENTS,
    { variables: { eventId: id }, skip },
  );
  const { data: coachesData, loading: coachesLoading } = useQuery(
    FETCH_REG_SPREADSHEET_COACHES,
    { variables: { eventId: id }, skip },
  );
  const { data: guestsData, loading: guestsLoading } = useQuery(
    FETCH_REG_SPREADSHEET_GUESTS,
    { variables: { eventId: id }, skip },
  );
  const { data: paymentsData, loading: paymentsLoading } = useQuery(
    ALL_PAYMENTS_FOR_REGISTRATION_OPTION_AGAINST_EVENT,
    { variables: { eventId }, skip },
  );
  const { data: competitionsData, loading: competitionsLoading } = useQuery(
    CATEGORY_WITH_EVENT_COMPETITIONS,
    { variables: { eventId }, skip },
  );

  const waitlist = useMemo(() => {
    const result = computeWaitlistData(competitionsData);
    return result;
  }, [competitionsData]);

  const event = eventData?.fetchEventDetail ?? {};
  const pendingSchoolCount = event.pendingSchoolRequestCount ?? 0;
  const pendingCaptainCount = event.activeSchoolRequestCount ?? 0;

  const registration = useMemo(
    () => computeRegistrationCounts(
      studentsData?.eventStudents ?? [],
      coachesData?.eventCoaches ?? [],
      guestsData?.eventGuests ?? [],
    ),
    [studentsData, coachesData, guestsData],
  );

  const unpaid = useMemo(
    () => computeUnpaidCounts(paymentsData?.paymentAgainstRegistrationOption ?? []),
    [paymentsData],
  );

  useHeaderComponentsMutation({
    title: 'PRE-TOURNAMENT CHECKLIST',
    backLink: `/event-manage/${id}`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const isLoading = eventLoading || studentsLoading || coachesLoading
    || guestsLoading || paymentsLoading || competitionsLoading;

  if (isLoading) {
    return <Loader />;
  }

  const counts = [
    pendingSchoolCount,
    pendingCaptainCount,
    registration.pendingApproval.total,
    registration.noSchool.total,
    unpaid.total,
    waitlist.total,
  ];
  const completedCount = counts.filter((c) => c === 0).length;

  return (
    <Provider>
      <Stack spacing={6} p={6} maxW="900px" mx="auto" bg="white" minH="100vh">
        <Box width="100%">
          <Text color="gray.600" textAlign="center">Complete all tasks to prepare for your tournament</Text>
        </Box>

        <Box width="100%">
          <ProgressIndicator completedCount={completedCount} />
        </Box>

        <Stack spacing={3} width="100%">
          <SimpleChecklistCard
            itemNumber={1}
            title="School Requests"
            description="Review and merge duplicate school requests"
            count={pendingSchoolCount}
            linkTo={`/event-manage/${id}/unapprove-schools`}
          />

          <SimpleChecklistCard
            itemNumber={2}
            title="Captain Requests"
            description="Approve team captains (first approved competitor per school)"
            count={pendingCaptainCount}
            linkTo={`/event-manage/${id}/unapprove-teams`}
          />

          <ExpandableChecklistCard
            itemNumber={3}
            title="Registration Approvals"
            description="Approve pending user registrations"
            count={registration.pendingApproval.total}
            linkTo={`/events/${id}/registration-report`}
            linkText="View registration report"
            details={registration.pendingApproval.details}
          />

          <ExpandableChecklistCard
            itemNumber={4}
            title="School Assignments"
            description="Assign schools to users without one"
            count={registration.noSchool.total}
            linkTo={`/events/${id}/registration-report`}
            linkText="View registration report"
            details={registration.noSchool.details}
          />

          <ExpandableChecklistCard
            itemNumber={5}
            title="Payment Collection"
            description="Collect outstanding payments"
            count={unpaid.total}
            linkTo={`/events/${id}/payment-report`}
            linkText="View payment report"
            details={unpaid.details}
          />

          <WaitlistCard
            itemNumber={6}
            totalWaitlisted={waitlist.total}
            waitlistedByCompetition={waitlist.byCompetition}
            eventId={id}
          />
        </Stack>
      </Stack>
    </Provider>
  );
};

export default PreTournamentChecklist;
